import { clientProjectAPI } from '@business/api/clientProject';
import { ProjectAPI } from '@business/api/project';
import { UserId } from '@business/entities/account';
import { ClientProjectResponse, CompactClientProjectResponse, ProjectId } from '@business/entities/clientProject';
import { ProjectStepResponse } from '@business/entities/project';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap, tap, map } from 'rxjs/operators';
import { TaskPriority } from '@business/constants/task';

export class ClientProjectBloc extends BaseBloc {
  private readonly clientProjectRepo = new Repository<CompactClientProjectResponse | ClientProjectResponse>({
    getItemId: project => project.id,
  });

  readonly projects$ = this.clientProjectRepo.items$;
  readonly selectProject = this.clientProjectRepo.selectItem;

  readonly selectStep = (projectId: ProjectId, stepId: string) =>
    this.clientProjectRepo.selectItem(projectId).pipe(
      map(project => {
        if (!project) return undefined;
        const allSteps = R.flatMap((project as ClientProjectResponse).sections || [], section => section.steps);
        return allSteps.find(s => s.id === stepId);
      }),
    );
  onReset() {
    this.clientProjectRepo.reset();
  }

  fetchProjects = () => {
    return clientProjectAPI
      .fetchClientProjects()
      .pipe(this.clientProjectRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_client_projects_error') }));
  };

  fetchProject = (id: ProjectId, appId?: string) => {
    return clientProjectAPI.fetchProject(id, appId).pipe(
      this.clientProjectRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_client_project_error') }),
    );
  };

  fetchStep = (projectId: ProjectId, id: string) => {
    return clientProjectAPI.fetchStep(id).pipe(
      //   this.projectRepo.ops.updateOne(sections => ({
      //     id: projectId,
      //     changes: () => ({ sections }),
      //   })),
      handleMessage({
        error: requestMessage('fetch_step'),
      }),
    );
  };

  toggleStep = (projectId: ProjectId, stepId: number, tenantId?: string, completeAll?: boolean) => {
    return clientProjectAPI.toggleStep(stepId, tenantId, completeAll).pipe(this.clientProjectRepo.ops.upsertOne(item => ({ item })));
  };

  createStep = (projectId: ProjectId, sectionId: string, data: Partial<ProjectStepResponse>, tenantId?: string, parentStepId?: string) => {
    return ProjectAPI.createStep(sectionId, data, parentStepId, tenantId).pipe(
      this.clientProjectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),

      handleMessage({
        type: requestMessage('create_template_section_step'),
      }),
    );
  };

  updateStep = (projectId: ProjectId, id: string, data: Partial<ProjectStepResponse>, tenantId?: string, success = false) => {
    return ProjectAPI.updateStep(id, data, tenantId).pipe(
      this.clientProjectRepo.ops.updateOne(section => ({
        id: projectId,
        changes: project => {
          const p = project as ClientProjectResponse;
          const sections = p.sections.map(s => {
            if (s.id == section.id) {
              return section;
            }
            return s;
          });
          return { sections };
        },
      })),

      success
        ? handleMessage({
            type: requestMessage('update_template_section_step'),
          })
        : handleMessage({
            error: requestMessage('save_step'),
          }),
    );
  };

  deleteStep = (projectId: ProjectId, id: string, tenantId?: string) => {
    return showDeleteConfirmation('Delete Step', 'Do you really want to remove this Step?').pipe(
      switchMap(() => ProjectAPI.deleteStep(id, tenantId)),
      this.clientProjectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),

      handleMessage({
        type: requestMessage('delete_template_section_step'),
      }),
    );
  };

  /* Sections */

  createSection = (projectId: ProjectId, name: string, tenantId?: string) => {
    return ProjectAPI.createSection(projectId, name, tenantId, true).pipe(
      this.clientProjectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),
      handleMessage({
        type: requestMessage('create_template_section'),
      }),
    );
  };

  updateSection = (projectId: ProjectId, sectionId: string, name: string, tenantId?: string) => {
    return ProjectAPI.updateSection(sectionId, name, tenantId).pipe(
      this.clientProjectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),

      handleMessage({
        type: requestMessage('update_template_section'),
      }),
    );
  };

  deleteSection = (projectId: ProjectId, id: string, tenantId?: string) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() => ProjectAPI.deleteSection(id, tenantId)),
      this.clientProjectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),

      handleMessage({
        type: requestMessage('delete_template_section'),
      }),
    );
  };

  sortSectionSteps = (projectId: ProjectId, sectionId: string, stepIds: string[], tenantId?: string) => {
    return ProjectAPI.sortSectionSteps(stepIds, tenantId).pipe(
      tap(() => {
        const template = this.clientProjectRepo.getItem(projectId)! as ClientProjectResponse;
        const affectedSection = R.find(template.sections, s => s.id === sectionId)!;
        const newSteps = affectedSection.steps.map(step => {
          return { ...step, menuOrder: R.findIndex(stepIds, i => i === step.id) };
        });

        const newSections = template.sections.map(sec => {
          if (sec.id === sectionId) {
            return { ...sec, steps: R.sortBy(newSteps, s => s.menuOrder) };
          }
          return sec;
        });

        this.clientProjectRepo.updateOne({
          id: projectId,
          changes: () => ({ sections: newSections }),
        });
      }),
      handleMessage({
        error: requestMessage('update_template_steps_sort'),
      }),
    );
  };

  assignStep = (projectId: ProjectId, stepId: string, userId: UserId) => {
    return ProjectAPI.assignStep(stepId, { userId }).pipe(
      this.clientProjectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),
    );
  };

  setProjectStepPriority = (priority: TaskPriority, stepId: string, projectId: ProjectId) => {
    return ProjectAPI.setProjectStepPriority(stepId, priority).pipe(
      this.clientProjectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),
      handleMessage({
        error: requestMessage('project_step_priority'),
      }),
    );
  };

  assignProjectStep = (projectId: ProjectId, stepId: string, data: string[]) => {
    return ProjectAPI.assignProjectStep(stepId, data).pipe(
      this.clientProjectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),
      handleMessage({
        error: requestMessage('update_template_steps_sort'),
      }),
    );
  };

  /* Steps  */
}
